import Search from "@mui/icons-material/Search";
import { Box, InputAdornment, Popover, TextField } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MergeTagAccordion } from "./merge-tag-accordion";
import { useKeyboardNavigation } from "./use-keyboard-navigation";

import type { TagGroup } from "./types";
import type { PopoverProps } from "@mui/material";
import type { ChangeEventHandler, KeyboardEvent, ReactElement } from "react";

export type MergeTagLookupProps = PopoverProps & {
  filterTags: (x: string) => TagGroup;
  onSelection: (value: string) => void;
  onClose: () => void;
};

export const MergeTagLookup = ({
  filterTags,
  onSelection,
  open,
  anchorEl,
  onClose,
}: MergeTagLookupProps): ReactElement => {
  const [value, setValue] = useState<string>("");
  const [expandAllSections, setExpandAllSections] = useState(false);

  const tags = useMemo(() => filterTags(value), [filterTags, value]);

  const handleSelection = useCallback(
    (selection: string): void => {
      onSelection(selection);
      setValue("");
    },
    [onSelection],
  );

  const { activeSection, navigateSection, setActiveSection } = useKeyboardNavigation(
    tags,
    expandAllSections,
    handleSelection,
  );

  useEffect(() => {
    setExpandAllSections(
      Object.values(tags).reduce((count, { groupTags }) => count + (groupTags?.length ?? 0), 0) < 5,
    );

    const sectionsWithResults = Object.entries(tags).filter(
      ([, { groupTags }]) => (groupTags.length ?? 0) > 0,
    );
    const autoExpand = sectionsWithResults.length === 1 ? sectionsWithResults[0]![0] : null;
    if (autoExpand) {
      setActiveSection({ section: autoExpand, index: 0 });
    }
  }, [tags, setActiveSection]);

  const handleClick = useCallback(
    (tagId: string): void => {
      handleSelection(tagId);
      setActiveSection({ section: null, index: null });
    },
    [handleSelection, setActiveSection],
  );

  const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event): void => setValue(event.target.value),
    [setValue],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>): void => {
      switch (event.key) {
        case "Tab":
        case "Enter":
        case "Return":
          event.preventDefault();
          navigateSection("enter");
          return;
        case "Up":
        case "ArrowUp":
          navigateSection("up");
          return;
        case "Down":
        case "ArrowDown":
          navigateSection("down");
          return;
        case "ArrowRight":
          navigateSection("right");
          return;
        case "ArrowLeft":
          navigateSection("left");
          return;
        default:
          return;
      }
    },
    [navigateSection],
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={(e, r) => {
        setValue("");
        setActiveSection({ section: null, index: null });
        onClose && onClose(e, r);
      }}
      disableRestoreFocus
      disableAutoFocus
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      sx={{ m: 1, zIndex: 9999, maxHeight: 450 }}
    >
      <Box sx={{ my: 1, px: 1 }}>
        <TextField
          size="small"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          fullWidth
          variant="outlined"
          id="tag-selector"
          placeholder="Search for a tag"
          autoFocus={true}
          onClick={() => setActiveSection({ section: null, index: null })}
          autoComplete="off"
        />
      </Box>
      <Box>
        {Object.entries(tags).map(([section, tagInfo]) => (
          <MergeTagAccordion
            key={section}
            active={activeSection.section === section}
            activeIndex={activeSection.index}
            section={section}
            tagInfo={tagInfo}
            defaultExpanded={expandAllSections && tagInfo.groupTags.length > 0}
            onSelect={handleClick}
          />
        ))}
      </Box>
    </Popover>
  );
};
